/**
 * The flow map API's client half: one map per guide, per project and per AI call campaign.
 *
 *   GET /api/admin/crm/flow-maps/:owner/:ownerId                         -> FlowMapView
 *   PUT /api/admin/crm/flow-maps/:owner/:ownerId  { baseVersion, doc }   -> FlowMapView
 *   GET /api/admin/crm/flow-maps/:owner/:ownerId/revisions               -> { revisions }
 *   GET /api/admin/crm/flow-maps/:owner/:ownerId/revisions/:version      -> FlowRevision
 *
 * Restoring an old version is an ordinary save of its document, not a route of
 * its own: it goes through the same version check and leaves the version it
 * replaced in the history, so an undo of an undo is one more click.
 *
 * `FlowMapView` mirrors `apps/api/src/modules/crm-flowmap/types.ts` field for
 * field. It is a copy rather than an import because the CRM app never imports
 * from the API, so a field added there is a field to add here.
 *
 * A save is optimistic: it carries the version it was based on, and when
 * somebody else saved in between, the server refuses with 409
 * `{ error: "flow_conflict", current }`, `current` being its own view of the map.
 * What the editor does with that answer is decided in
 * `crm/sections/_shared/flowmap/saveState.ts`, not here. The other refusals are
 * 400 `invalid_flow` (a document that breaks the model's rules), 403 `forbidden`
 * and 404 `not_found` (the guide or project is gone, or hidden from this user).
 *
 * The document type is imported as a TYPE only: this file must not pull the
 * widget package into every screen that happens to import the CRM's API layer.
 */
import type { FlowMapDoc } from "@wa/widgets/flow-map/model";
import { api } from "./api";

/** What a map hangs off. An AI call campaign's map is the course of its calls (`lib/aiCallFlow.ts`). */
export type FlowOwner = "guide" | "project" | "aiCampaign";

/** One map as the CRM reads it. The same shape answers a load, a save and a conflict. */
export interface FlowMapView {
  owner: FlowOwner;
  ownerId: string;
  /** The stored row's id; null until the first save. */
  id: string | null;
  /** 0 = never saved. The next save sends this back as `baseVersion`. */
  version: number;
  /** Always a valid document: the stored one normalised, or an empty map. */
  doc: FlowMapDoc;
  /** ISO time of the current version; null until the first save. */
  updatedAt: string | null;
  updatedByName: string | null;
  /** Whether the server would accept a save from this user (manage:crm.kb, manage:crm.projects or manage:crm.calls by owner). */
  canEdit: boolean;
}

function flowMapPath(owner: FlowOwner, ownerId: string): string {
  return `/admin/crm/flow-maps/${owner}/${encodeURIComponent(ownerId)}`;
}

/** The map of one guide or project. A never-saved map answers as an empty map at version 0, not as a 404. */
export function getFlowMap(owner: FlowOwner, ownerId: string): Promise<FlowMapView> {
  return api.get<FlowMapView>(flowMapPath(owner, ownerId));
}

/** Replace the map. `baseVersion` is the version this document was edited from (0 for the first save). */
export function saveFlowMap(owner: FlowOwner, ownerId: string, baseVersion: number, doc: FlowMapDoc): Promise<FlowMapView> {
  return api.put<FlowMapView>(flowMapPath(owner, ownerId), { baseVersion, doc });
}

/** One past version of a map, without its document. */
export interface FlowRevisionSummary {
  id: string;
  /** The version this row holds: what the map was before the save that replaced it. */
  version: number;
  /** Who saved it, or null when the handbook seed wrote it and no person has since. */
  savedByName: string | null;
  at: string;
}

/** One past version with its document: what a preview draws and what a restore saves. */
export interface FlowRevision extends FlowRevisionSummary {
  doc: FlowMapDoc;
}

/** The map's past versions, newest first. A map with no history answers with an empty list. */
export function listFlowRevisions(owner: FlowOwner, ownerId: string): Promise<{ revisions: FlowRevisionSummary[] }> {
  return api.get<{ revisions: FlowRevisionSummary[] }>(`${flowMapPath(owner, ownerId)}/revisions`);
}

/** One past version. A version that has fallen out of the kept 50 answers 404. */
export function getFlowRevision(owner: FlowOwner, ownerId: string, version: number): Promise<FlowRevision> {
  return api.get<FlowRevision>(`${flowMapPath(owner, ownerId)}/revisions/${version}`);
}
